import { useState, useEffect, useRef, useLayoutEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Check, Loader2, MapPin, Pencil } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOK_EMPTY = { navn: "", adresse: "", lat: null, lng: null, radius_meter: 200, by: "", aktiv: true, ip_adresser: "" };
const BYER = ["København", "Aarhus", "Odense", "Aalborg", "Kolding", "Andet"];

export function MiniKort({ lat, lng, onPick }) {
  const containerRef = useRef(null);
  const leafletRef = useRef(null);
  const markerRef = useRef(null);

  useLayoutEffect(() => {
    if (!containerRef.current || leafletRef.current) return;
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
    }
    import("leaflet").then(L => {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
        iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
        shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
      });
      const initLat = lat || 56.0;
      const initLng = lng || 10.0;
      const map = L.map(containerRef.current).setView([initLat, initLng], lat ? 15 : 7);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "© OpenStreetMap" }).addTo(map);
      if (lat && lng) {
        markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
        markerRef.current.on("dragend", () => { const p = markerRef.current.getLatLng(); onPick(p.lat, p.lng); });
      }
      map.on("click", e => {
        const { lat: la, lng: lo } = e.latlng;
        if (markerRef.current) markerRef.current.setLatLng([la, lo]);
        else {
          markerRef.current = L.marker([la, lo], { draggable: true }).addTo(map);
          markerRef.current.on("dragend", () => { const p = markerRef.current.getLatLng(); onPick(p.lat, p.lng); });
        }
        onPick(la, lo);
      });
      leafletRef.current = { map, L };
    });
    return () => { if (leafletRef.current) { leafletRef.current.map.remove(); leafletRef.current = null; markerRef.current = null; } };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!leafletRef.current || lat == null || lng == null) return;
    const { map, L } = leafletRef.current;
    if (markerRef.current) markerRef.current.setLatLng([lat, lng]);
    else {
      markerRef.current = L.marker([lat, lng], { draggable: true }).addTo(map);
      markerRef.current.on("dragend", () => { const p = markerRef.current.getLatLng(); onPick(p.lat, p.lng); });
    }
    map.setView([lat, lng], 15);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lat, lng]);

  return <div ref={containerRef} style={{ height: 220, borderRadius: 10, overflow: "hidden" }} />;
}

export function LokationerAdmin({ token }) {
  const [loks, setLoks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(LOK_EMPTY);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const searchTimer = useRef(null);
  const hdrs = { Authorization: `Bearer ${token}` };

  const load = () => {
    axios.get(`${API}/admin/clock-lokationer`, { headers: hdrs })
      .then(r => setLoks(r.data.lokationer || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setForm(f => ({ ...f, adresse: v }));
    clearTimeout(searchTimer.current);
    if (v.length < 3) { setSuggestions([]); return; }
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(v)}&format=json&limit=5&countrycodes=dk`, { headers: { "Accept-Language": "da" } });
        setSuggestions(await r.json());
      } catch {}
      setSearching(false);
    }, 400);
  };

  const pickSuggestion = (s) => {
    const adresse = s.display_name.split(",").slice(0, 3).join(", ");
    setForm(f => ({ ...f, adresse, lat: parseFloat(s.lat), lng: parseFloat(s.lon) }));
    setQuery(adresse);
    setSuggestions([]);
  };

  const handleMapPick = async (lat, lng) => {
    setForm(f => ({ ...f, lat, lng }));
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`, { headers: { "Accept-Language": "da" } });
      const data = await r.json();
      if (data.display_name) {
        const adresse = data.display_name.split(",").slice(0, 3).join(", ");
        setForm(f => ({ ...f, lat, lng, adresse }));
        setQuery(adresse);
      }
    } catch {}
  };

  const openCreate = () => {
    setEditId(null);
    setForm(LOK_EMPTY);
    setQuery("");
    setSuggestions([]);
    setShowForm(true);
  };

  const openEdit = (lok) => {
    setEditId(lok.lok_id);
    setForm({ navn: lok.navn, adresse: lok.adresse || "", lat: lok.lat, lng: lok.lng, radius_meter: lok.radius_meter || 200, by: lok.by || "", aktiv: lok.aktiv, ip_adresser: lok.ip_adresser || "" });
    setQuery(lok.adresse || "");
    setSuggestions([]);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.navn.trim() || form.lat == null || form.lng == null) return;
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/admin/clock-lokationer/${editId}`, form, { headers: hdrs });
      } else {
        await axios.post(`${API}/admin/clock-lokationer`, form, { headers: hdrs });
      }
      await load();
      setForm(LOK_EMPTY);
      setQuery("");
      setEditId(null);
      setShowForm(false);
    } catch { alert("Kunne ikke gemme lokation"); }
    setSaving(false);
  };

  const handleDelete = async (lok_id) => {
    if (!window.confirm("Slet denne standard-lokation?")) return;
    setDeletingId(lok_id);
    try {
      await axios.delete(`${API}/admin/clock-lokationer/${lok_id}`, { headers: hdrs });
      setLoks(prev => prev.filter(l => l.lok_id !== lok_id));
    } catch { alert("Kunne ikke slette"); }
    setDeletingId(null);
  };

  const toggleAktiv = async (lok) => {
    try {
      await axios.put(`${API}/admin/clock-lokationer/${lok.lok_id}`, { ...lok, aktiv: !lok.aktiv }, { headers: hdrs });
      setLoks(prev => prev.map(l => l.lok_id === lok.lok_id ? { ...l, aktiv: !l.aktiv } : l));
    } catch {}
  };

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-bold text-foreground flex items-center gap-2"><MapPin className="w-4 h-4 text-[#FF2A6D]" />Standard-lokationer</h2>
          <p className="text-[13px] text-muted-foreground mt-0.5">Lokationer der kan vælges hurtigt under medarbejdere</p>
        </div>
        {!showForm && (
          <button onClick={openCreate}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors">
            <Plus className="w-3.5 h-3.5" /> Ny lokation
          </button>
        )}
      </div>

      {showForm && (
        <div className="rounded-xl border border-[#FF2A6D]/30 bg-[#FF2A6D]/5 p-4 mb-4 flex flex-col gap-3">
          <p className="text-[13px] font-semibold text-foreground">{editId ? "Rediger lokation" : "Ny standard-lokation"}</p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Navn *</label>
              <input value={form.navn} onChange={e => setForm(f => ({ ...f, navn: e.target.value }))} placeholder="fx. Salon København"
                className="w-full bg-background border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">By</label>
              <select value={form.by} onChange={e => setForm(f => ({ ...f, by: e.target.value }))}
                className="w-full bg-background border border-border rounded-lg px-3 h-8 text-sm focus:outline-none">
                <option value="">— vælg —</option>
                {BYER.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="col-span-2 relative">
              <label className="text-[13px] text-muted-foreground mb-1 block">Søg adresse</label>
              <div className="relative">
                <input value={query} onChange={handleQueryChange} placeholder="Bredgade 12, 1260 København..."
                  className="w-full bg-background border border-border rounded-lg px-3 pr-8 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
                {searching && <Loader2 className="absolute right-2 top-2 w-4 h-4 animate-spin text-muted-foreground" />}
              </div>
              {suggestions.length > 0 && (
                <div className="absolute z-20 left-0 right-0 top-full mt-1 bg-background border border-border rounded-xl shadow-xl overflow-hidden">
                  {suggestions.map((s, i) => (
                    <button key={i} onClick={() => pickSuggestion(s)}
                      className="w-full text-left px-3 py-2 text-[13px] hover:bg-secondary transition-colors border-b border-border last:border-0">
                      {s.display_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="col-span-2">
              <label className="text-[13px] text-muted-foreground mb-1 block">Bekræft på kortet</label>
              <MiniKort lat={form.lat} lng={form.lng} onPick={handleMapPick} />
            </div>
            <div>
              <label className="text-[13px] text-muted-foreground mb-1 block">Radius (m)</label>
              <input type="number" value={form.radius_meter} min="50" max="2000"
                onChange={e => setForm(f => ({ ...f, radius_meter: parseInt(e.target.value) || 200 }))}
                className="w-full bg-background border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
            {form.lat != null && (
              <div className="flex items-end pb-0.5">
                <p className="text-[12px] text-muted-foreground">{form.lat?.toFixed(5)}, {form.lng?.toFixed(5)}</p>
              </div>
            )}
            <div className="col-span-2">
              <label className="text-[13px] text-muted-foreground mb-1 block">Tilladte IP-adresser <span className="text-muted-foreground/60">(kommasepareret — godkender check-in via wi-fi)</span></label>
              <input value={form.ip_adresser} onChange={e => setForm(f => ({ ...f, ip_adresser: e.target.value }))}
                placeholder="fx. 192.168.1.1, 10.0.0.5"
                className="w-full bg-background border border-border rounded-lg px-3 h-8 text-sm focus:outline-none focus:ring-1 focus:ring-[#FF2A6D]" />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => { setShowForm(false); setEditId(null); setForm(LOK_EMPTY); setQuery(""); setSuggestions([]); }}
              className="px-3 py-1.5 rounded-lg text-[13px] border border-border hover:bg-secondary transition-colors">Annuller</button>
            <button onClick={handleSave} disabled={saving || !form.navn.trim() || form.lat == null}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg bg-[#FF2A6D] text-white text-[13px] font-semibold hover:bg-[#e02560] transition-colors disabled:opacity-60">
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} {editId ? "Gem ændringer" : "Gem"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-[13px] text-muted-foreground py-4">Henter lokationer...</p>
      ) : loks.length === 0 ? (
        <p className="text-[13px] text-muted-foreground italic py-2">Ingen standard-lokationer oprettet endnu.</p>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-secondary/50">
                <th className="text-left px-4 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Navn</th>
                <th className="text-left px-4 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Adresse</th>
                <th className="text-left px-4 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">By</th>
                <th className="text-left px-4 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Radius</th>
                <th className="text-center px-4 py-2.5 text-[13px] font-semibold text-muted-foreground uppercase tracking-wide">Aktiv</th>
                <th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {loks.map((lok, i) => (
                <tr key={lok.lok_id} className={`border-b border-border hover:bg-secondary/30 transition-colors ${i % 2 === 1 ? "bg-secondary/10" : ""}`}>
                  <td className="px-4 py-2.5 font-medium">{lok.navn}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-[13px]">{lok.adresse || "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-[13px]">{lok.by || "—"}</td>
                  <td className="px-4 py-2.5 text-muted-foreground text-[13px] tabular-nums">{lok.radius_meter}m</td>
                  <td className="px-4 py-2.5 text-center">
                    <button onClick={() => toggleAktiv(lok)}
                      title={lok.aktiv ? "Aktiv — klik for at deaktivere" : "Inaktiv — klik for at aktivere"}
                      className={`w-5 h-5 rounded flex items-center justify-center border transition-colors mx-auto ${lok.aktiv ? "bg-[#FF2A6D] border-[#FF2A6D]" : "border-border hover:border-[#FF2A6D]/50"}`}>
                      {lok.aktiv && <Check className="w-3 h-3 text-white" />}
                    </button>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => openEdit(lok)}
                        className="p-1.5 rounded-full text-muted-foreground hover:text-[#FF2A6D] hover:bg-[#FF2A6D]/10 transition-colors">
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => handleDelete(lok.lok_id)} disabled={deletingId === lok.lok_id}
                        className="p-1.5 rounded-full text-muted-foreground hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-40">
                        {deletingId === lok.lok_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
